/**
 * BreadcrumbLink:
 *   - OLD: `asChild` (boolean, uses Radix Slot for render delegation)
 *   - NEW: `render` (ReactElement, uses @base-ui useRender pattern)
 *
 * BreadcrumbEllipsis:
 *   - OLD: static <span> with MoreHorizontal icon (no interactivity)
 *   - NEW: interactive <Menu> dropdown; pass <MenuItem> children to populate it
 *
 * BreadcrumbPage:
 *   - OLD: includes role="link" aria-disabled="true"
 *   - NEW: omits those attrs (aria-current="page" retained)
 */
'use client'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import type * as React from 'react'
import { cn } from '../lib/utils'
import { MoreHorizontalIcon } from './icon'
import { IconButton } from './icon-button'
import { Menu, MenuTrigger, MenuPopup } from './menu'

export function Breadcrumb({
  ...props
}: React.ComponentProps<'nav'>): React.ReactElement {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

export function BreadcrumbList({
  className,
  ...props
}: React.ComponentProps<'ol'>): React.ReactElement {
  return (
    <ol
      className={cn(
        'text-on-surface-secondary text-md flex flex-wrap items-center gap-3 wrap-break-word',
        className,
      )}
      data-slot="breadcrumb-list"
      {...props}
    />
  )
}

export function BreadcrumbItem({
  className,
  ...props
}: React.ComponentProps<'li'>): React.ReactElement {
  return (
    <li
      className={cn('inline-flex items-center', className)}
      data-slot="breadcrumb-item"
      {...props}
    />
  )
}

export function BreadcrumbLink({
  className,
  render,
  ...props
}: useRender.ComponentProps<'a'>): React.ReactElement {
  const defaultProps = {
    className: cn('transition-colors select-none', className),
    'data-slot': 'breadcrumb-link',
  }

  return useRender({
    defaultTagName: 'a',
    props: mergeProps<'a'>(defaultProps, props),
    render,
  })
}

export function BreadcrumbPage({
  className,
  ...props
}: React.ComponentProps<'span'>): React.ReactElement {
  return (
    <span
      aria-current="page"
      className={cn('text-on-surface font-normal select-none', className)}
      data-slot="breadcrumb-page"
      {...props}
    />
  )
}

export function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>): React.ReactElement {
  return (
    <li
      aria-hidden="true"
      className={cn('opacity-80 select-none [&>svg]:size-5', className)}
      data-slot="breadcrumb-separator"
      role="presentation"
      {...props}
    >
      {children ?? '/'}
    </li>
  )
}

export function BreadcrumbEllipsis({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}): React.ReactElement {
  return (
    <Menu>
      <MenuTrigger
        render={
          <IconButton
            className={cn('text-on-surface-secondary', className)}
            data-slot="breadcrumb-ellipsis"
          >
            <MoreHorizontalIcon />
            More
          </IconButton>
        }
      />
      <MenuPopup>{children}</MenuPopup>
    </Menu>
  )
}
